import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaBoxesPacking, FaClock, FaGlobe, FaHeadset, FaTruckFast, FaWarehouse } from "react-icons/fa6";
import TrustUs from "../images/trust_us.jpg";
import Logo1 from "../images/cahoot_logo.png";
import Logo2 from "../images/client-01.png";
import Logo3 from "../images/client-02.png";
import Logo4 from "../images/client-03.png";
import Logo5 from "../images/client-04.png";
import Logo6 from "../images/client-05.png";
import Logo7 from "../images/client-06.png";
import Logo8 from "../images/client-07.png";
import GlobalNetworkGraphic from "../images/graphics/global-network.svg";
import MatchingWorkflowGraphic from "../images/graphics/matching-workflow.svg";
import PageHero from "../components/pageHero";

const logos = [Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7, Logo8];

const valueCards = [
  { icon: <FaWarehouse />, title: "Global warehouse discovery", text: "Search by region, products, services, and availability." },
  { icon: <FaHeadset />, title: "CS-assisted matching", text: "Your team can shortlist providers and steer the quote process." },
  { icon: <FaTruckFast />, title: "Modern fulfillment fit", text: "Capture omnichannel, returns, and specialized storage needs." }
];

function HomeMarketplace() {
  const [activeStep, setActiveStep] = useState(0);

  const workflowSteps = [
    "Browse warehouses by region, services, and fit.",
    "Submit one structured buyer brief.",
    "Let CS shortlist, quote, and close."
  ];

  return (
    <>
      <Helmet>
        <title>3PL Search | Warehouse Discovery and Quote Support</title>
        <meta name="description" content="Browse 3PL warehouses, submit your requirement, and let the CS team shortlist providers and finalize quotes." />
      </Helmet>

      <div className="home-page">
        <PageHero
          eyebrow="Global 3PL sourcing platform"
          title="Discover warehouses, qualify demand, and close more 3PL deals."
          description="Buyers can browse the marketplace first and use one primary buyer brief when they are ready. Your CS team then matches providers, shapes the quote, and helps finalize the deal."
          primaryAction={{ to: "/finder", label: "Browse warehouses" }}
          secondaryAction={{ to: "/3pl_booking", label: "Request a tailored match" }}
          image={GlobalNetworkGraphic}
          imageAlt="Global warehouse network infographic"
        >
          <div className="hero-metrics">
            <div className="metric-card"><strong>Buyer-friendly</strong><span>Clear for founders, operators, and enterprise teams.</span></div>
            <div className="metric-card"><strong>CS-ready</strong><span>Structured buyer briefs help your team quote faster.</span></div>
            <div className="metric-card"><strong>Global-ready</strong><span>Built for customers looking anywhere in the world.</span></div>
          </div>
        </PageHero>

        <section className="logo-section">
          <div className="container">
            <div className="section-heading centered">
              <span className="eyebrow-badge">Trust signals</span>
              <h2>A cleaner experience that feels credible to all customer levels.</h2>
            </div>
            <div className="logo-marquee modern">
              <div className="logo-track">
                {logos.concat(logos).map((logo, index) => (
                  <div className="logo-item modern" key={`${logo}-${index}`}>
                    <img src={logo} alt={`Partner logo ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <div className="container">
            <div className="row g-4">
              {valueCards.map((card) => (
                <div className="col-md-4" key={card.title}>
                  <div className="surface-card h-100">
                    <div className="feature-icon">{card.icon}</div>
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="content-section muted">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-6">
                <img className="section-image" src={TrustUs} alt="Consultative 3PL support" />
              </div>
              <div className="col-lg-6">
                <div className="section-heading">
                  <span className="eyebrow-badge">How the business model works</span>
                  <h2>The site captures interest, the directory builds trust, and CS closes the deal.</h2>
                </div>
                <div className="info-panel">
                  <div><FaGlobe /><span>Customers explore regions and warehouse capabilities.</span></div>
                  <div><FaBoxesPacking /><span>Operational details are captured for better matching.</span></div>
                  <div><FaClock /><span>Timelines help your team prioritize urgent launches.</span></div>
                  <div><FaHeadset /><span>CS can suggest quotes, negotiate, and finalize the partner.</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <div className="container">
            <div className="row g-5 align-items-start">
              <div className="col-lg-6">
                <div className="section-heading">
                  <span className="eyebrow-badge">No repeated buyer forms</span>
                  <h2>One clear path for discovery, one clear path for guided matching.</h2>
                </div>
                <p className="section-copy">
                  We removed the repeated enquiry experience from the homepage. Buyers can now:
                </p>
                <div className="workflow-list">
                  {workflowSteps.map((step, index) => (
                    <button
                      key={step}
                      type="button"
                      className={`workflow-item button-card ${activeStep === index ? "active" : ""}`}
                      onClick={() => setActiveStep(index)}
                    >
                      <span>{index + 1}</span>
                      <p>{step}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="surface-card h-100">
                  <img className="section-image section-image-contained" src={MatchingWorkflowGraphic} alt="Buyer matching workflow infographic" />
                  <div className="lead-form-actions mt-4">
                    <Link className="site-primary-cta" to={activeStep === 0 ? "/finder" : "/3pl_booking"}>
                      {activeStep === 0 ? "Start with the directory" : "Open the buyer brief"}
                    </Link>
                    <Link className="site-secondary-cta" to="/contact">Speak with CS</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default HomeMarketplace;
